package no.vareflow.web

import no.vareflow.app.domain.t
import no.vareflow.app.domain.AppLanguage
import no.vareflow.app.domain.I18n

// Firebase in the browser: the "compat" JavaScript SDK loaded by index.html from gstatic
// (global `firebase`), used through `dynamic`. Only the Firebase calls live here and in WebCloud;
// the rules for what to write and how to read it come from the shared module (CloudMapping,
// CountOutbox), exactly as on the phone.

/** Public web-app configuration of the Firebase project "vareflow" (not secrets). */
object FirebaseWebConfig {
    const val API_KEY = "AIzaSyCnEPT7FTiXceYVP5_0U0f6hI8KQX-NES4"
    const val AUTH_DOMAIN = "vareflow.firebaseapp.com"
    const val PROJECT_ID = "vareflow"
    const val STORAGE_BUCKET = "vareflow.firebasestorage.app"
    const val MESSAGING_SENDER_ID = "6260825514"
    const val APP_ID = "1:6260825514:web:dd4d8fbae716c3b349dfd3"

    fun asJs(): dynamic {
        val o: dynamic = js("({})")
        o.apiKey = API_KEY
        o.authDomain = AUTH_DOMAIN
        o.projectId = PROJECT_ID
        o.storageBucket = STORAGE_BUCKET
        o.messagingSenderId = MESSAGING_SENDER_ID
        o.appId = APP_ID
        return o
    }
}

/**
 * App Check (web): proves to Firebase that requests come from the real Vareflow page.
 * Provider: reCAPTCHA Enterprise ("Fraud Defense"), score-based key "Vareflow web" for
 * vareflow.no, www.vareflow.no, vareflow.web.app, vareflow.firebaseapp.com.
 * The site key is public (it is in every page); there is no secret on the web side.
 *
 * Phase 1 = MONITOR only: Firebase is NOT set to enforce, so a missing/failed token never blocks
 * anything – the console just shows how many requests are verified. Enforcing comes later,
 * after the Android app also sends tokens (Play Integrity).
 */
object AppCheckConfig {
    const val SITE_KEY = "6LeLru0tAAAAAItq3OPM7jVwqemIdxd11OA3h8tf"
}

/**
 * Activates App Check on [app] (the main app and the temporary sign-up apps). Never throws:
 * if the App Check script is missing or blocked, Vareflow works on without it.
 * On localhost (or with `?appcheckdebug`) a debug token is used instead of reCAPTCHA – the browser
 * console then prints a token that can be registered under App Check → Manage debug tokens.
 */
fun activateAppCheck(app: dynamic) {
    try {
        val sdk = firebaseSdk() ?: return
        if (jsTypeOf(sdk.appCheck) != "function" || app == null) return
        val host = (window.location.hostname as? String).orEmpty()
        val search = (window.location.search as? String).orEmpty()
        val local = host == "localhost" || host == "127.0.0.1"
        // On the developer's own machine (gradle jsBrowserDevelopmentRun) reCAPTCHA does not work and
        // an unregistered debug token gets HTTP 403. App Check only monitors, so it is simply left off
        // there; ?appcheckdebug turns on debug mode for a token registered in the Firebase console.
        if (local && !search.contains("appcheckdebug")) return
        if (search.contains("appcheckdebug")) js("self.FIREBASE_APPCHECK_DEBUG_TOKEN = true")
        val siteKey = AppCheckConfig.SITE_KEY
        val provider: dynamic = js("new firebase.appCheck.ReCaptchaEnterpriseProvider(siteKey)")
        sdk.appCheck(app).activate(provider, true)
    } catch (e: Throwable) {
        console.warn("App Check not active: " + (e.message ?: e.toString()))
    }
}

/** The global `firebase` namespace, or null when the SDK could not be loaded (offline, blocked). */
fun firebaseSdk(): dynamic = js("(typeof firebase !== 'undefined') ? firebase : null")

/** Placeholder in a write map for Firestore's server time. */
object ServerTime

/** Firestore data (plain JS values, Timestamps, arrays, objects) → Kotlin values for CloudMapping. */
fun jsToKotlin(value: dynamic): Any? {
    if (value == null || value == undefined) return null
    return when (jsTypeOf(value)) {
        "string" -> value as String
        "number" -> (value as Number).toDouble()
        "boolean" -> value as Boolean
        "object" -> when {
            js("Array").isArray(value) as Boolean -> {
                val length = value.length as Int
                (0 until length).map { jsToKotlin(value[it]) }
            }
            jsTypeOf(value.toMillis) == "function" -> (value.toMillis() as Number).toLong()
            else -> {
                val keys: dynamic = js("Object").keys(value)
                val length = keys.length as Int
                val map = LinkedHashMap<String, Any?>()
                for (i in 0 until length) {
                    val key = keys[i] as String
                    map[key] = jsToKotlin(value[key])
                }
                map
            }
        }
        else -> null
    }
}

/** A document snapshot's data as a Kotlin map, with its id under "id" (like the phone's dataWithId). */
fun docToMap(doc: dynamic): Map<String, Any?> {
    @Suppress("UNCHECKED_CAST")
    val map = (jsToKotlin(doc.data()) as? Map<String, Any?>)?.toMutableMap() ?: mutableMapOf()
    map["id"] = doc.id as String
    return map
}

/** Kotlin write data → plain JS for Firestore. Long becomes a JS number; [ServerTime] the server timestamp. */
fun kotlinToJs(value: Any?): dynamic = when (value) {
    null -> null
    is ServerTime -> firebaseSdk().firestore.FieldValue.serverTimestamp()
    is String -> value
    is Boolean -> value
    is Number -> value.toDouble()
    is Map<*, *> -> {
        val o: dynamic = js("({})")
        value.forEach { (k, v) -> o[k.toString()] = kotlinToJs(v) }
        o
    }
    is Iterable<*> -> {
        val array: dynamic = js("[]")
        value.forEach { array.push(kotlinToJs(it)) }
        array
    }
    else -> value.toString()
}

/** `{ merge: true }` for set(). */
fun mergeOption(): dynamic {
    val o: dynamic = js("({})")
    o.merge = true
    return o
}

/** Firebase error code without the "auth/" or "firestore/" prefix, e.g. "permission-denied". */
fun firebaseErrorCode(error: dynamic): String {
    if (error == null) return ""
    val code = error.code
    return if (code == null || code == undefined) "" else (code as String).substringAfter('/')
}

fun firebaseErrorMessage(error: dynamic): String {
    if (error == null) return no.vareflow.app.domain.t("Ukjent feil", "Unknown error")
    val message = error.message
    val text = if (message == null || message == undefined) error.toString() as String else message as String
    // Firebase's own texts start with "Firebase: …"; people should not see the service name.
    return text.removePrefix("FirebaseError: ").removePrefix("Firebase: ").trim()
}

/** [length] characters from [alphabet], using the browser's cryptographic random generator. */
fun secureRandomString(length: Int, alphabet: String): String {
    val values: dynamic = js("new Uint32Array(length)")
    js("crypto").getRandomValues(values)
    return (0 until length).map { alphabet[((values[it] as Number).toDouble() % alphabet.length).toInt()] }.joinToString("")
}

/** A seed from the browser's cryptographic random generator (for Kotlin's Random). */
fun secureSeed(): Long {
    val values: dynamic = js("new Uint32Array(2)")
    js("crypto").getRandomValues(values)
    return ((values[0] as Number).toLong() shl 32) or (values[1] as Number).toLong()
}
